import React, { useEffect, useState } from 'react'
import { useApiCall } from '../../../component/custom-hooks/api-call';
import { card, EditableCell, Section } from '.';
import InnerLoader from '../../../component/ui/loader/inner-loader';
import DataNotFound from '../../../component/ui/loader/data-not-found';
import { Search, Store } from 'lucide-react';

const Vendors = ({ toast = () => { }, inpStyle, C }) => {
    const [isLoading, setIsLoading] = useState(true);
    const selStyle = { ...inpStyle, cursor: "pointer" };
    const apiCall = useApiCall();

    const [dropdownOptions, setDropdownOptions] = useState({});

    const getDropdownOptions = async () => {
        try {
            const res = await apiCall({ url: `cash-flow/dropdown`, method: "GET" });
            setDropdownOptions(res?.dropdown || []);
        } catch (error) {
            console.error(`Error fetching dropdown options:`, error);
            return [];
        }
    };

    // Category actions
    const [vendors, setVendors] = useState([]);
    const [modalVendor, setModalVendor] = useState(null);
    const [vendorForm, setVendorForm] = useState({});
    const openNewVendor = () => {
        setVendorForm({ name: "", category_id: null, location_id: null, default_amount: null, payment_method: "", is_active: true });
        setModalVendor(true);
    };
    const [vendorSearch, setVendorSearch] = useState("");
    const filteredVendors = vendors.filter(v =>
        String(v?.name || "")?.toLowerCase()?.includes(vendorSearch?.toLowerCase()) ||
        String(v?.category_name || "")?.toLowerCase()?.includes(vendorSearch?.toLowerCase()) ||
        String(v?.location_name || "")?.toLowerCase()?.includes(vendorSearch?.toLowerCase()) ||
        String(v?.payment_method || "")?.toLowerCase()?.includes(vendorSearch?.toLowerCase())
    );

    const fetchVendors = async () => {
        setIsLoading(true);
        try {
            const res = await apiCall({ url: "cash-flow/vendors/list", method: "GET" });
            setVendors(res.data || []);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching vendors:", error);
            setIsLoading(false);
        }
    };

    const updateVendor = async (id, field, val) => {
        // Extract value if it's an event object
        const value = val?.target?.value !== undefined ? val.target.value : val;

        const newdata = vendors.map(c => c.id === id ? { ...c, [field]: value } : c);
        setVendors(newdata);
        const updatedVendor = newdata.find(c => c.id === id);

        // Prepare data to send to API
        const dataToSend = {
            name: updatedVendor?.name,
            category_id: updatedVendor?.category_id,
            location_id: updatedVendor?.location_id,
            default_amount: updatedVendor?.default_amount,
            payment_method: updatedVendor?.payment_method,
            is_active: updatedVendor?.is_active,
            id: updatedVendor?.id,
        };

        try {
            await apiCall({
                url: `cash-flow/vendors/update`,
                method: "POST",
                data: dataToSend
            });
            toast("Vendor updated", "success");
        } catch (error) {
            console.error("Error updating vendor:", error);
            toast("Error updating vendor", "error");
            fetchVendors();
        }
    };

    const addVendor = async () => {
        try {
            await apiCall({
                url: "cash-flow/vendors/add",
                method: "POST",
                data: vendorForm
            });
            toast("Vendor added", "success");
            setModalVendor(false);
            setVendorForm({});
            fetchVendors();
        } catch (error) {
            toast(error?.response?.data?.message || "Error adding vendor", "error");
        }
    };

    const toggleVendorActive = async (id) => {
        const newdata = vendors.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c);
        setVendors(newdata);
        try {
            await apiCall({
                url: `cash-flow/vendors/${id}/update-status`,
                method: "POST",
            });
            toast("Vendor updated", "success");
        } catch (error) {
            console.error("Error toggling vendor active status:", error?.message || error);
            toast("Error updating vendor status", "error");
            fetchVendors();
        }
    };

    useEffect(() => {
        fetchVendors();
        getDropdownOptions();
    }, []);

    return (
        <Section
            id="vendors"
            icon={<Store size={18} />}
            title="Vendors"
            desc="Recurring vendors — default amount and payment method auto-fill when adding a bill"
            C={C}
            card={card}
            Btn={
                <button onClick={() => { openNewVendor() }}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 9, border: `1px dashed ${C.border}`, background: "#f9fafb", fontSize: 12, fontWeight: 600, color: C.textSec, cursor: "pointer" }}>
                    + Add Vendor
                </button>
            }
        >
            {isLoading ? (
                <div className="w-full h-[420px] flex items-center justify-center">
                    <InnerLoader />
                </div>
            ) : vendors.length < 1 || !vendors.length ? (
                <div className="w-full h-[420px] flex items-center justify-cente pb-5">
                    <DataNotFound title={"vendors"} />
                </div>
            ) : (
                <>
                    {/* Search */}
                    <div style={{ position: "relative", marginBottom: 12 }}>
                        <span
                            style={{
                                position: "absolute",
                                left: 10,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: C.textMut,
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            <Search size={14} />
                        </span>
                        <input value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} placeholder="Search vendors…"
                            style={{ ...inpStyle, width: "100%", paddingLeft: 32 }} />
                    </div>

                    {/* Column headers */}
                    <div style={{ display: "grid", gridTemplateColumns: "1.8fr 110px 120px 100px 90px 80px", gap: 0, background: "#f9f8f7", borderRadius: "10px 10px 0 0", border: `1px solid ${C.border}`, padding: "0 14px", marginBottom: 0 }}>
                        {["Vendor", "Category", "Location", "Default Amt", "Method", ""].map((h, i) => (
                            <div key={i} style={{ padding: "8px 8px", fontSize: 9, fontWeight: 700, color: C.textMut, letterSpacing: 0.6, textTransform: "uppercase" }}>{h}</div>
                        ))}
                    </div>

                    <div style={{ borderRadius: "0 0 10px 10px", border: `1px solid ${C.border}`, borderTop: "none", overflow: "hidden", marginBottom: 12 }}>
                        {filteredVendors.map((v, i) => (
                            <div key={v.id} style={{
                                display: "grid", gridTemplateColumns: "1.8fr 110px 120px 100px 90px 80px",
                                gap: 0, padding: "0 14px",
                                background: !v.is_active ? "#f9fafb" : i % 2 === 0 ? "#fff" : "#fafaf9",
                                borderBottom: i < filteredVendors.length - 1 ? `1px solid ${C.border}` : "none",
                                opacity: v.is_active ? 1 : 0.5,
                            }}>
                                {/* Name */}
                                <div style={{ padding: "11px 8px", display: "flex", alignItems: "center" }}>
                                    <EditableCell value={v?.name} onSave={val => updateVendor(v?.id, "name", val)} style={{ fontSize: 12, fontWeight: 600, color: C.textPri }} />
                                </div>
                                {/* Category */}
                                <div style={{ padding: "8px 8px", display: "flex", alignItems: "center" }}>
                                    <select value={v.category_id || ""} onChange={e => updateVendor(v.id, "category_id", parseInt(e.target.value) || null)}
                                        style={{ ...selStyle, fontSize: 11, padding: "4px 6px", width: "100%" }}>
                                        <option value="">Select Category</option>
                                        {dropdownOptions?.category?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                {/* Location */}
                                <div style={{ padding: "8px 8px", display: "flex", alignItems: "center" }}>
                                    <select value={v.location_id || ""} onChange={e => updateVendor(v.id, "location_id", parseInt(e.target.value) || null)}
                                        style={{ ...selStyle, fontSize: 11, padding: "4px 6px", width: "100%" }}>
                                        <option value="">All / General</option>
                                        {dropdownOptions?.lcoation?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>
                                {/* Default Amount */}
                                <div style={{ padding: "8px 8px", display: "flex", alignItems: "center" }}>
                                    <EditableCell value={v.default_amount || ""} onSave={val => updateVendor(v.id, "default_amount", val ? parseFloat(val) : null)} type="number" placeholder="Variable" style={{ fontSize: 9, color: C.textPri }} />
                                </div>
                                {/* Method */}
                                <div style={{ padding: "8px 8px", display: "flex", alignItems: "center" }}>
                                    <select value={v.payment_method} onChange={e => updateVendor(v.id, "payment_method", e.target.value)}
                                        style={{ ...selStyle, fontSize: 11, padding: "4px 6px", width: "100%" }}>
                                        {dropdownOptions?.method?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </select>
                                </div>
                                {/* Toggle */}
                                <div style={{ padding: "8px 8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <button onClick={() => toggleVendorActive(v.id)}
                                        style={{ padding: "3px 9px", borderRadius: 20, border: `1px solid ${v.is_active ? "#fecaca" : C.border}`, background: v.is_active ? "#fef2f2" : "#f0fdf4", fontSize: 10, fontWeight: 600, color: v.is_active ? C.red : C.green, cursor: "pointer" }}>
                                        {v.is_active ? "Off" : "On"}
                                    </button>
                                </div>
                            </div>
                        ))}
                        {filteredVendors.length === 0 && (
                            <div style={{ padding: "28px", textAlign: "center", color: C.textMut, fontSize: 13 }}>No vendors found</div>
                        )}
                    </div>
                </>
            )}

            {/* Vendor modal */}
            {modalVendor && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                    <div style={{ background: "#fff", borderRadius: 16, padding: "24px", width: "100%", maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: "0 0 18px", fontSize: 16, fontWeight: 800, color: C.textPri }}>
                            Add Vendor
                        </h3>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                Vendor Name
                            </div>
                            <input type={'text'} value={vendorForm.name} onChange={e => setVendorForm({ ...vendorForm, name: e.target.value })} placeholder={"Vendor name"} style={{ ...inpStyle, width: "100%" }} />
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                Default Amount
                            </div>
                            <input type={'number'} value={vendorForm.default_amount || ""} onChange={e => setVendorForm({ ...vendorForm, default_amount: e.target.value ? parseFloat(e.target.value) : null })} placeholder={"Default amount (optional)"} style={{ ...inpStyle, width: "100%" }} />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                    Category
                                </div>
                                <select value={vendorForm.category_id || 1} onChange={e => setVendorForm({ ...vendorForm, category_id: parseInt(e.target.value) })} style={{ ...selStyle, width: "100%" }}>
                                    {dropdownOptions?.category?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                    Location
                                </div>
                                <select value={vendorForm.location_id || ""} onChange={e => setVendorForm({ ...vendorForm, location_id: parseInt(e.target.value) })} style={{ ...selStyle, width: "100%" }}>
                                    <option value="">All / General</option>
                                    {dropdownOptions?.lcoation?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                    Method
                                </div>
                                <select value={vendorForm.payment_method || "Cheque"} onChange={e => setVendorForm({ ...vendorForm, payment_method: e.target.value })} style={{ ...selStyle, width: "100%" }}>
                                    {dropdownOptions?.method?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                onClick={() => setModalVendor(null)}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, cursor: "pointer", color: C.textSec }}
                            >
                                Cancel
                            </button>

                            <button onClick={addVendor}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: "none", background: C.accent, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Section>
    )
}

export default Vendors
